import { FC, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { IdParamsType } from 'utils/types';
import { Tag } from 'features/common/types';
import {
	PunchupTagManager,
	CreateTagData,
	UpdateTagData,
} from 'features/common/components/Punchup/composites/editors/PunchupTagManager';
import useGetEvent from 'features/common/hooks/useGetEvent';
import { useTagsApi } from '../hooks/useTagsApi';
import { useOptionalAccordionSection } from 'features/common/components/Punchup/primitives/layout/PunchupAccordion';
import { queryClient } from 'index';
import { TagType, CreateTagRequest } from '../types';
import { addTagsToEvent, createTag, removeTagsFromEvent, updateTag } from '../services';
import { callWithFlash, FlashFn } from 'features/common/utils/callWithFlash';

/**
 * EventTagsEditor
 *
 * Thin wrapper around PunchupTagManager for the Event Tags accordion section.
 * Each action is applied immediately — the chip strip updating is the success feedback.
 * On error, the accordion section flashes red.
 */
export const EventTagsEditor: FC = () => {
	const { t } = useTranslation();
	const { id } = useParams<IdParamsType>();
	const { event } = useGetEvent();
	const { tags: allTags, refetch: refetchTags } = useTagsApi();
	const section = useOptionalAccordionSection();

	const eventTags: Tag[] = event?.tags || [];
	const flashFn: FlashFn = (state, opts) => section?.flash(state, opts);

	const availableTags = useMemo(() => {
		if (!Array.isArray(allTags)) return [];
		return allTags
			.filter((tag: Tag) => tag.tag_type === TagType.EVENT)
			.map((tag: Tag) => ({ ...tag, id: tag.id ?? (tag as any).ID }));
	}, [allTags]);

	const handleAdd = (tagId: number) =>
		callWithFlash(
			async () => {
				await addTagsToEvent(id, { tag_ids: [tagId] });
				queryClient.invalidateQueries({ queryKey: ['event', id] });
			},
			flashFn,
			{ title: t('tag-added', 'Tag added') },
			{ title: t('tag-add-failed', 'Failed to add tag') }
		);

	const handleRemove = (tagId: number) =>
		callWithFlash(
			async () => {
				await removeTagsFromEvent(id, { tag_ids: [tagId] });
				queryClient.invalidateQueries({ queryKey: ['event', id] });
			},
			flashFn,
			{ title: t('tag-removed', 'Tag removed') },
			{ title: t('tag-remove-failed', 'Failed to remove tag') }
		);

	const handleCreate = (data: CreateTagData): Promise<Tag> =>
		callWithFlash(
			async () => {
				const newTag = await createTag({ ...data, tag_type: TagType.EVENT } as CreateTagRequest);
				await addTagsToEvent(id, { tag_ids: [newTag.id] });
				await refetchTags();
				queryClient.invalidateQueries({ queryKey: ['event', id] });
				return newTag;
			},
			flashFn,
			{ title: t('tag-created', 'Tag created') },
			{ title: t('tag-create-failed', 'Failed to create tag') }
		);

	const handleUpdate = (tagId: number, data: UpdateTagData) =>
		callWithFlash(
			async () => {
				await updateTag(tagId, data);
				await refetchTags();
				queryClient.invalidateQueries({ queryKey: ['event', id] });
			},
			flashFn,
			{ title: t('tag-updated', 'Tag updated') },
			{ title: t('tag-update-failed', 'Failed to update tag') }
		);

	return (
		<PunchupTagManager
			availableTags={availableTags}
			selectedTags={eventTags}
			onAdd={handleAdd}
			onRemove={handleRemove}
			onCreate={handleCreate}
			onUpdate={handleUpdate}
			onDirtyChange={(isDirty) => section?.setDirty(isDirty)}
		/>
	);
};
